import { useState, useEffect } from "react";
import SiderBar from "../../components/siderbarcomponents";
import {
  subscribeToQuotations,
  updateQuotationStatus,
  Quotation,
} from "../../firebase/firebaseService";
import {
  FaFileAlt,
  FaSearch,
  FaTimes,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaDollarSign,
  FaCheckCircle,
  FaUserCheck,
  FaClock,
  FaBed,
  FaBath,
  FaChevronRight,
} from "react-icons/fa";

// ─── Helpers ───────────────────────────────────────────────────────────────
const formatDate = (date: Date) =>
  new Date(date).toLocaleDateString("es-ES", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const STATUS_CONFIG: Record<string, { label: string; classes: string; dot: string }> = {
  pending:   { label: "Pendiente",   classes: "bg-amber-100 text-amber-700",   dot: "bg-amber-400" },
  contacted: { label: "Contactado",  classes: "bg-blue-100 text-blue-700",     dot: "bg-blue-400" },
  completed: { label: "Completada",  classes: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-400" },
};

const getStatus = (s: string) => STATUS_CONFIG[s] ?? STATUS_CONFIG["pending"];

// ─── Component ─────────────────────────────────────────────────────────────
export default function CotizacionesAdmin() {
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selected, setSelected] = useState<Quotation | null>(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const unsub = subscribeToQuotations((data) => {
      setQuotations(data);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const handleStatusChange = async (quote: Quotation, newStatus: string) => {
    if (!quote.id) return;
    setUpdating(true);
    try {
      await updateQuotationStatus(quote.id, newStatus);
      // Update local selected so panel reflects change immediately
      setSelected((prev) => prev?.id === quote.id ? { ...prev, status: newStatus } : prev);
    } finally {
      setUpdating(false);
    }
  };

  const filtered = quotations.filter((q) => {
    const matchSearch =
      q.name.toLowerCase().includes(search.toLowerCase()) ||
      q.email.toLowerCase().includes(search.toLowerCase()) ||
      q.phone.includes(search);
    const matchStatus = filterStatus === "all" || q.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const pending   = quotations.filter((q) => q.status === "pending").length;
  const contacted = quotations.filter((q) => q.status === "contacted").length;
  const completed = quotations.filter((q) => q.status === "completed").length;
  const totalValue = quotations.reduce((s, q) => s + (q.price || 0), 0);

  return (
    <div className="flex-col lg:flex-row flex min-h-screen bg-slate-50">
      <SiderBar />

      <div className="flex-1 overflow-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-700 to-indigo-600 px-8 py-8">
          <h1 className="text-3xl font-bold text-white">Cotizaciones</h1>
          <p className="text-blue-200 text-sm mt-1">Solicitudes de servicio recibidas desde la web</p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
            {[
              { label: "Total",       value: quotations.length, icon: FaFileAlt,    bg: "bg-white/20" },
              { label: "Pendientes",  value: pending,           icon: FaClock,      bg: "bg-amber-400/30" },
              { label: "Contactados", value: contacted,         icon: FaUserCheck,  bg: "bg-blue-400/30" },
              { label: "Completadas", value: completed,         icon: FaCheckCircle,bg: "bg-emerald-400/30" },
            ].map((s) => (
              <div key={s.label} className="bg-white/20 backdrop-blur rounded-2xl p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 ${s.bg} rounded-xl flex items-center justify-center`}>
                    <s.icon className="text-white" size={16} />
                  </div>
                  <div>
                    <p className="text-blue-100 text-[13px] font-medium">{s.label}</p>
                    <p className="text-white text-2xl font-bold">{s.value}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-6 lg:p-8">
          {/* Filtros */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1 max-w-md">
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
              <input
                type="text"
                placeholder="Buscar por nombre, email o teléfono..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-400 shadow-sm"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-400 shadow-sm text-gray-600 font-medium"
            >
              <option value="all">Todos los estados</option>
              <option value="pending">Pendiente</option>
              <option value="contacted">Contactado</option>
              <option value="completed">Completada</option>
            </select>
          </div>

          {/* Tabla */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin w-8 h-8 border-4 border-blue-300 border-t-blue-600 rounded-full" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-gray-400">
                <FaFileAlt size={38} className="mb-3 text-gray-200" />
                <p className="font-medium text-[15px]">Sin cotizaciones</p>
                <p className="text-[13px] mt-1">Prueba cambiando los filtros de búsqueda</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="text-left px-6 py-4 text-[13px] font-semibold text-gray-500 uppercase tracking-wider">Cliente</th>
                      <th className="text-left px-6 py-4 text-[13px] font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Contacto</th>
                      <th className="text-left px-6 py-4 text-[13px] font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Servicio</th>
                      <th className="text-left px-6 py-4 text-[13px] font-semibold text-gray-500 uppercase tracking-wider">Precio</th>
                      <th className="text-left px-6 py-4 text-[13px] font-semibold text-gray-500 uppercase tracking-wider">Estado</th>
                      <th className="text-left px-6 py-4 text-[13px] font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Fecha</th>
                      <th className="px-6 py-4" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filtered.map((q) => {
                      const st = getStatus(q.status || "pending");
                      return (
                        <tr
                          key={q.id}
                          className="hover:bg-blue-50/30 transition-colors cursor-pointer"
                          onClick={() => setSelected(q)}
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                                {q.name.slice(0, 2).toUpperCase()}
                              </div>
                              <div>
                                <p className="text-[15px] font-semibold text-gray-800">{q.name}</p>
                                <p className="text-[13px] text-gray-400 truncate max-w-[160px]">{q.address}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 hidden md:table-cell">
                            <div className="space-y-1">
                              <div className="flex items-center gap-1.5 text-[13px] text-gray-500">
                                <FaEnvelope size={10} className="text-gray-400" />{q.email}
                              </div>
                              <div className="flex items-center gap-1.5 text-[13px] text-gray-500">
                                <FaPhone size={10} className="text-gray-400" />{q.phone}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 hidden lg:table-cell">
                            <p className="text-[15px] font-medium text-gray-700">{q.service}</p>
                            <p className="text-[13px] text-gray-400">{q.frequency}</p>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-[15px] font-bold text-emerald-600">${q.price}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[13px] font-semibold ${st.classes}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                              {st.label}
                            </span>
                          </td>
                          <td className="px-6 py-4 hidden sm:table-cell">
                            <span className="text-[13px] text-gray-400">{formatDate(q.createdAt)}</span>
                          </td>
                          <td className="px-6 py-4">
                            <FaChevronRight size={13} className="text-gray-300" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Footer stats */}
          <div className="mt-4 flex items-center gap-2 text-[13px] text-gray-400 px-1">
            <span>{filtered.length} cotización{filtered.length !== 1 ? "es" : ""}</span>
            <span>·</span>
            <span className="font-semibold text-emerald-600">
              Valor total: ${totalValue.toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      {/* ── Panel de detalle (slide-in) ────────────────────────────────── */}
      {/* Overlay */}
      {selected && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
          onClick={() => setSelected(null)}
        />
      )}

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col transition-transform duration-300 ease-out ${
          selected ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {selected && (
          <>
            {/* Panel header */}
            <div className="bg-gradient-to-r from-blue-700 to-indigo-600 px-6 py-5 flex items-center justify-between shrink-0">
              <div>
                <h2 className="text-white font-bold text-lg">{selected.name}</h2>
                <p className="text-blue-200 text-[13px] mt-0.5">{formatDate(selected.createdAt)}</p>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
              >
                <FaTimes size={14} />
              </button>
            </div>

            {/* Status bar */}
            <div className="px-6 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
              <span className="text-[13px] text-gray-500 font-medium">Estado actual:</span>
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[13px] font-bold ${getStatus(selected.status || "pending").classes}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${getStatus(selected.status || "pending").dot}`} />
                {getStatus(selected.status || "pending").label}
              </span>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {/* Contacto */}
              <Section title="Información de Contacto">
                <InfoRow icon={FaEnvelope} label="Email" value={selected.email} />
                <InfoRow icon={FaPhone}   label="Teléfono" value={selected.phone} />
                <InfoRow icon={FaMapMarkerAlt} label="Dirección" value={selected.address} />
              </Section>

              {/* Servicio */}
              <Section title="Servicio Solicitado">
                <InfoRow icon={FaFileAlt} label="Tipo" value={selected.service} />
                <InfoRow icon={FaClock}   label="Frecuencia" value={selected.frequency} />
                <InfoRow icon={FaBed}     label="Habitaciones" value={String(selected.bedrooms)} />
                <InfoRow icon={FaBath}    label="Baños" value={String(selected.bathrooms)} />
              </Section>

              {/* Extras */}
              {(selected.insideOven || selected.insideFridge || selected.insideCabinets || selected.windows) && (
                <Section title="Extras Solicitados">
                  <div className="flex flex-wrap gap-2">
                    {selected.insideOven     && <Tag label="Horno interior" />}
                    {selected.insideFridge   && <Tag label="Nevera interior" />}
                    {selected.insideCabinets && <Tag label="Gabinetes" />}
                    {selected.windows        && <Tag label="Ventanas" />}
                  </div>
                </Section>
              )}

              {/* Precio y contacto */}
              <Section title="Precio y Preferencias">
                <InfoRow icon={FaDollarSign} label="Precio estimado" value={`$${selected.price}`} valueClass="text-emerald-600 font-bold text-[17px]" />
                <InfoRow
                  icon={FaPhone}
                  label="¿Desea ser contactado?"
                  value={selected.wantsContact ? "Sí" : "No"}
                  valueClass={selected.wantsContact ? "text-blue-600 font-semibold" : "text-gray-500"}
                />
              </Section>
            </div>

            {/* Action buttons */}
            <div className="p-6 border-t border-gray-100 space-y-3 shrink-0">
              <p className="text-[13px] text-gray-400 text-center font-medium mb-1">Cambiar estado de la cotización</p>

              {(selected.status === "pending" || !selected.status) && (
                <button
                  onClick={() => handleStatusChange(selected, "contacted")}
                  disabled={updating}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold text-[15px] hover:from-blue-700 hover:to-indigo-700 transition-all disabled:opacity-50 shadow-lg shadow-blue-200 flex items-center justify-center gap-2"
                >
                  <FaUserCheck size={15} />
                  {updating ? "Actualizando..." : "Marcar como Contactado"}
                </button>
              )}

              {selected.status === "contacted" && (
                <button
                  onClick={() => handleStatusChange(selected, "completed")}
                  disabled={updating}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-[15px] hover:from-emerald-600 hover:to-teal-600 transition-all disabled:opacity-50 shadow-lg shadow-emerald-200 flex items-center justify-center gap-2"
                >
                  <FaCheckCircle size={15} />
                  {updating ? "Actualizando..." : "Marcar como Completada"}
                </button>
              )}

              {selected.status !== "pending" && selected.status && (
                <button
                  onClick={() => handleStatusChange(selected, "pending")}
                  disabled={updating}
                  className="w-full py-2.5 rounded-xl border border-gray-200 text-gray-500 font-semibold text-[13px] hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Volver a Pendiente
                </button>
              )}

              <button
                onClick={() => setSelected(null)}
                className="w-full py-2.5 rounded-xl border border-gray-200 text-gray-500 font-semibold text-[13px] hover:bg-gray-50 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wider mb-3">{title}</h3>
      <div className="bg-gray-50 rounded-2xl p-4 space-y-3">{children}</div>
    </div>
  );
}

function InfoRow({
  icon: Icon,
  label,
  value,
  valueClass = "text-gray-700 font-medium",
}: {
  icon: any;
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-7 h-7 rounded-lg bg-white border border-gray-100 flex items-center justify-center shrink-0 mt-0.5">
        <Icon size={12} className="text-gray-400" />
      </div>
      <div>
        <p className="text-[12px] text-gray-400 font-medium">{label}</p>
        <p className={`text-[15px] ${valueClass}`}>{value}</p>
      </div>
    </div>
  );
}

function Tag({ label }: { label: string }) {
  return (
    <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-[13px] font-semibold border border-blue-100">
      {label}
    </span>
  );
}
